import { themeGet } from '../../utils/theme-get.js';
import React__default from 'react';
import styled from 'styled-components';
import Box from '../Box/Box.js';
import FuegokitLink from '../Link/Link.js';
import Text from '../Text/Text.js';
import { Card } from '../Card/Card.js';

const StyledUseCaseCard = styled(Card).withConfig({
  displayName: "UseCaseCard__StyledUseCaseCard",
  componentId: "sc-1hzthpg-0"
})(["list-style:none;padding:", " ", " ", " ", ";position:relative;max-width:330px;flex:1 1 0px;display:grid;gap:", ";border-radius:", ";border:1px solid ", ";border-top:", ";border-top-color:", ";box-shadow:", ";transition:350ms;min-height:326px;background-color:", ";&:hover{background-color:", ";box-shadow:", ";}&:focus-within{background-color:", ";box-shadow:", ";}&:active{background-color:", ";box-shadow:", ";}grid-template-areas:'header' 'content' 'footer';@media (min-width:", "){}@media (min-width:", "){}@media (min-width:", "){max-width:440px;}@media (min-width:", "){}"], themeGet('space.5'), themeGet('space.4'), themeGet('space.3'), themeGet('space.4'), themeGet('space.2'), themeGet('radii.3'), themeGet('colors.border.default'), p => p.hasTopBorder === true ? 'none' : '1px solid', p => p.hasTopBorder === true ? 'none' : themeGet('colors.border.default'), p => p.raised === true ? themeGet('shadows.elevation.shadow.raised') : '', themeGet('colors.elevation.surface.sunken'), themeGet('colors.elevation.surface.raised.hovered'), themeGet('shadows.elevation.shadow.overlay'), themeGet('colors.elevation.surface.raised.hovered'), themeGet('shadows.elevation.shadow.overflow.default'), themeGet('colors.background.neutral.subtle.pressed'), themeGet('shadows.elevation.shadow.overflow.default'), themeGet('breakpoints.0'), themeGet('breakpoints.1'), themeGet('breakpoints.2'), themeGet('breakpoints.3'));
const productCategoryGradients = {
  appfire: `linear-gradient(305.08deg, #1fda96 12.81%, #3fb3f2 97.78%)`,
  workflowAutomation: `linear-gradient(83.16deg, #8AEECA -0.85%, #1DCB8C 100%);`,
  publishing: `linear-gradient(83.16deg, #E23D3D -0.85%, #B7226A 99.47%)`,
  portfolioManagement: `linear-gradient(83.16deg, #F29BB6 -0.85%, #FADDE6 64.28%, #E64477 100%)`,
  itsm: `linear-gradient(83.16deg, #CFE7FC -0.85%, #20549A 100%)`,
  documentManagement: `linear-gradient(83.16deg, #BAEA9B -0.85%, #63A83B 100%)`,
  developerTools: `linear-gradient(83.16deg, #F9D95D -0.85%, #FAB34C 64.28%, #C67306 100%)`,
  biReporting: `linear-gradient(83.16deg, #FCB18F -0.85%, #F96C2C 100%)`,
  agile: `linear-gradient(83.16deg, #8AE8EE -0.85%, #0D7EBE 100%)`,
  adminTools: `linear-gradient(83.16deg, #F48E8E -0.85%, #B7226A 99.47%)`,
  default: `linear-gradient(83.16deg, #3FB3F2 -0.85%, #0C66E4 100%)`
};
const TopBorder = styled(Box).withConfig({
  displayName: "UseCaseCard__TopBorder",
  componentId: "sc-1hzthpg-1"
})(["min-height:11px;width:100.9%;margin-left:-1px;position:absolute;top:0;left:0;border-top-left-radius:", ";border-top-right-radius:", ";border-top:1px solid transparent;background:", ";"], themeGet('radii.3'), themeGet('radii.3'), p => p.productCategory && {}.hasOwnProperty.call(productCategoryGradients, p.productCategory) ? productCategoryGradients[p.productCategory] : `linear-gradient(305.08deg, #1fda96 12.81%, #3fb3f2 97.78%)`);
const MainLink = styled(FuegokitLink).withConfig({
  displayName: "UseCaseCard__MainLink",
  componentId: "sc-1hzthpg-2"
})(["color:", ";display:flex;align-items:center;z-index:1;&::after{position:absolute;top:0;left:0;right:0;bottom:0;content:' ';}&:hover{color:", ";}&:focus{outline:2px solid ", ";border-radius:", ";color:", ";}margin-top:auto;grid-row:5 / 6;grid-area:footer;"], themeGet('colors.link.default'), themeGet('colors.link.default'), themeGet('colors.border.focused'), themeGet('radii.1'), themeGet('colors.link.default'));
const StyledHeading = styled(Text).withConfig({
  displayName: "UseCaseCard__StyledHeading",
  componentId: "sc-1hzthpg-3"
})(["font-size:", ";font-weight:", ";line-height:", ";color:", ";grid-area:header;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:3rem;"], p => {
  if (p.titleSize === 'default') return themeGet('fontSizes.6');
  if (p.titleSize === 'small') return themeGet('fontSizes.4');
  return themeGet('fontSizes.6');
}, themeGet('fontWeights.semibold'), themeGet('lineHeights.condensed'), themeGet('colors.text.default'));
const CardBodyContent = styled(Text).withConfig({
  displayName: "UseCaseCard__CardBodyContent",
  componentId: "sc-1hzthpg-4"
})(["color:", ";margin-top:", ";grid-row:3 / 4;display:flex;justify-content:center;grid-area:content;"], themeGet('colors.text.subtle'), themeGet('space.2'));
const SVG = styled.svg.withConfig({
  displayName: "UseCaseCard__SVG",
  componentId: "sc-1hzthpg-5"
})(["user-select:none;overflow:visible;"]);
/**
 *
 * Getting Started UseCase Card renders a list item or other semantically appropriate card item that has a clickable usecase,
 * while maintaining semantic html structure and accessibility features for users of assistive technologies should be provided affordances
 * that ensure that the card and content are usable and inclusive.
 *
 * Typically used in a grid of 2 or 3.
 */
const UseCaseCard = ({
  title,
  label,
  url,
  as = 'li',
  bodyContent,
  hasTopBorder = false,
  raised = false,
  productCategory = 'default',
  titleSize = 'default',
  actionHandler,
  testId
}) => {
  const wrapperTestId = `${testId}-wrapper`;
  const titleTestId = `${testId}-title`;
  return /*#__PURE__*/React__default.createElement(StyledUseCaseCard, {
    as: as,
    raised: raised,
    hasTopBorder: hasTopBorder,
    "data-testid": wrapperTestId,
    onClick: () => actionHandler ? actionHandler() : null
  }, hasTopBorder ? /*#__PURE__*/React__default.createElement(TopBorder, {
    productCategory: productCategory
  }) : null, /*#__PURE__*/React__default.createElement(StyledHeading, {
    titleSize: titleSize,
    as: "h2",
    "data-testid": titleTestId
  }, title), /*#__PURE__*/React__default.createElement(CardBodyContent, {
    as: "p"
  }, bodyContent), /*#__PURE__*/React__default.createElement(MainLink, {
    href: url,
    target: "_blank"
  }, /*#__PURE__*/React__default.createElement(SVG, {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.031 7c-.1358-.00013-.2704.02661-.3959.07869-.1254.05207-.2394.12846-.3352.22475-.0958.0963-.1717.2106-.2231.33632-.0515.12573-.07759.26039-.0768.39624.0013.27657.1117.54146.3072.73712.1954.19567.4602.3063.7368.30788l3.121.014.014 3.121c.0016.2766.1122.5414.3079.7368.1956.1955.4605.3059.7371.3072.1362.0008.2712-.0254.3972-.0771.126-.0518.2404-.128.3368-.2242.0963-.0963.1726-.2107.2245-.3366.0518-.1259.0782-.2609.0775-.3971l-.019-4.161c-.0021-.2765-.1129-.54109-.3084-.73661-.1955-.19553-.4601-.3063-.7366-.30839L11.035 7h-.004Z",
    fill: "currentColor"
  }), /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "m13.364 8.292-7.072 7.071c-.18729.1878-.29246.4423-.29246.7075s.10517.5197.29246.7075c.39.39 1.024.39 1.415 0l7.071-7.071c.1388-.14022.2332-.31831.2712-.51194.038-.19363.018-.39418-.0575-.57649-.0755-.18231-.2031-.33827-.3669-.44831-.1638-.11004-.3565-.16927-.5538-.17026-.265-.00021-.5193.10482-.707.292Z",
    fill: "currentColor"
  })), label));
};
UseCaseCard.displayName = "UseCaseCard";
UseCaseCard.displayName = 'UseCaseCard';
var UseCaseCard$1 = UseCaseCard;

export { UseCaseCard$1 as default };
